import { api } from './api';
import type { PupilLogin } from '@/components/students/PupilLoginsDialog';

export interface Student {
  temporary_password?: string | null
  // what the pupil types at login (short identifier when they have no email)
  login?: string | null
  id: string;
  user_id: string;
  student_id?: string;
  full_name: string;
  email: string;
  grade_level: string;
  gender?: string;
  date_of_birth?: string;
  birthplace?: string;
  is_active: boolean;
  created_at: string;
  enrollment_status?: string;
  photo_url?: string;
  profile?: StudentProfile;
  parents: ParentInfo[];
  academic_stats: StudentAcademicStats;
  recent_mood_average?: number;
  sel_goals_progress?: number;
  behavior_score?: number;
  recent_achievements?: string[];
  enrolled_courses?: string[];
  assigned_teachers?: string[];
}

export interface StudentProfile {
  id: string;
  student_id: string;
  learning_style?: string;
  preferred_subjects?: string[];
  overall_gpa?: number;
  strengths?: string;
  areas_for_improvement?: string;
  emotional_check_ins?: string;
  medical_notes?: string;
  dietary_restrictions?: string;
  emergency_contact?: string;
  transportation_method?: string;
  extracurricular_activities?: string[];
}

export interface ParentInfo {
  id: string;
  full_name: string;
  email: string;
  phone?: string;
  relationship?: string;
}

export interface StudentAcademicStats {
  current_gpa: number;
  attendance_rate: number | null; // null until a register has been taken
  completed_lessons: number;
  total_lessons: number;
  completed_assessments: number;
  average_assessment_score: number;
  strengths: string[];
  improvement_areas: string[];
  subject_grades: Record<string, number>;
  last_login?: string;
  last_lesson_completed?: string;
  upcoming_assessments: number;
}

export interface StudentCreate {
  email?: string;
  full_name: string;
  password?: string;
  grade_level: string;
  gender?: string;
  date_of_birth?: string;
  birthplace?: string;
  student_id_number?: string;
  parent_emails?: string[];
  profile?: Partial<StudentProfile>;
}

export interface StudentUpdate {
  full_name?: string;
  grade_level?: string;
  gender?: string;
  date_of_birth?: string;
  birthplace?: string;
  enrollment_status?: string;
  student_id_number?: string;
  profile?: Partial<StudentProfile>;
}

export interface StudentListResponse {
  items: Student[];
  total: number;
  skip: number;
  limit: number;
}

export interface StudentProgressReport {
  student_id: string;
  reporting_period: string;
  academic: {
    overall_grade: string;
    gpa: number;
    subjects: Record<string, { grade: string; progress: number }>;
    completed_assignments: number;
    total_assignments: number;
    average_score: number;
  };
  social_emotional: {
    mood_average: number;
    sel_goals_completed: number;
    sel_goals_total: number;
    emotional_growth: string;
    peer_interaction: string;
  };
  attendance: {
    present_days: number;
    total_days: number;
    attendance_rate: number;
    tardies: number;
    excused_absences: number;
  };
  teacher_comments: Array<{
    teacher: string;
    subject: string;
    comment: string;
  }>;
  recommendations: string[];
}

export interface ParentCode {
  code: string;
  expires_at: string | null;
  uses_left: number;
  child_name: string;
  school_name: string;
}

class StudentService {
  async getStudents(params: {
    skip?: number;
    limit?: number;
    search?: string;
    grade_level?: string;
    classroom_id?: string;
    enrollment_status?: string;
    learning_style?: string;
    gpa_min?: number;
    gpa_max?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<StudentListResponse> {
    const response = await api.get<StudentListResponse>('/students', { params });
    return response.data;
  }

  async getStudent(id: string): Promise<Student> {
    const response = await api.get<Student>(`/students/${id}`);
    return response.data;
  }

  async createStudent(data: StudentCreate): Promise<Student> {
    const response = await api.post<Student>('/students', data);
    return response.data;
  }

  async updateStudent(id: string, data: StudentUpdate): Promise<Student> {
    const response = await api.patch<Student>(`/students/${id}`, data);
    return response.data;
  }

  async deleteStudent(id: string): Promise<void> {
    await api.delete(`/students/${id}`);
  }

  async activateStudent(id: string): Promise<void> {
    await api.post(`/students/${id}/activate`);
  }

  async getProgressReport(
    id: string,
    startDate?: string,
    endDate?: string
  ): Promise<StudentProgressReport> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const response = await api.get<StudentProgressReport>(
      `/students/${id}/progress-report?${params}`
    );
    return response.data;
  }

  async assignParent(
    studentId: string,
    parentEmail: string,
    relationship: string = 'parent'
  ): Promise<void> {
    await api.post(`/students/${studentId}/assign-parent`, {
      parent_email: parentEmail,
      relationship
    });
  }

  /** The code a pupil's parents sign up with to be linked to them (2 uses, 90 days). */
  async getParentCode(studentId: string): Promise<ParentCode> {
    const response = await api.post<ParentCode>(`/students/${studentId}/parent-code`);
    return response.data;
  }

  async getAnalytics(): Promise<any> {
    const response = await api.get('/students/analytics/overview');
    return response.data;
  }

  async bulkImportStudents(file: File, classroomId?: string): Promise<{ success: number; failed: number; assigned_to_classroom?: number; errors?: string[]; total_errors?: number; credentials?: PupilLogin[] }> {
    const formData = new FormData();
    formData.append('file', file);
    if (classroomId) {
      formData.append('classroom_id', classroomId);
    }
    
    const response = await api.post<{ success: number; failed: number; assigned_to_classroom?: number; errors?: string[]; total_errors?: number; credentials?: PupilLogin[] }>(
      '/students/bulk-import',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  /** A new temporary password for a pupil who lost theirs (school staff). */
  async resetPupilPassword(id: string): Promise<PupilLogin> {
    const response = await api.post<PupilLogin>(`/students/${id}/reset-password`);
    return response.data;
  }

  async exportStudents(format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    const response = await api.get(`/students/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  }

  async getStudentSchedule(id: string): Promise<any> {
    const response = await api.get(`/students/${id}/schedule`);
    return response.data;
  }

  async getStudentGrades(id: string, subjectId?: string): Promise<any> {
    const params = subjectId ? { subject_id: subjectId } : {};
    const response = await api.get(`/students/${id}/grades`, { params });
    return response.data;
  }

  async getStudentSELData(id: string): Promise<any> {
    const response = await api.get(`/students/${id}/sel-data`);
    return response.data;
  }

  async uploadPhoto(studentId: string, file: File): Promise<{ photo_url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ photo_url: string }>(`/students/${studentId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }
}

export const studentService = new StudentService();